
import jsPDF from 'jspdf';
import QRCode from 'qrcode';

interface DisputeData {
  disputeId: string;
  patientName: string;
  patientDOB: string;
  patientAddress: string;
  providerName: string;
  providerFax: string;
  requestedChange: string;
  justification: string;
  flaggedItems: any[];
}

export const generateDisputePDF = async (disputeData: DisputeData): Promise<Blob> => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  const maxWidth = pageWidth - (margin * 2);
  
  // Generate QR code as base64 PNG
  const trackingUrl = `https://qr.md/d/${disputeData.disputeId}`;
  const qrCodeDataUrl = await QRCode.toDataURL(trackingUrl, {
    width: 150,
    margin: 1,
    color: {
      dark: '#000000',
      light: '#FFFFFF'
    }
  });

  // Header
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('HIPAA REQUEST FOR AMENDMENT TO PROTECTED HEALTH INFORMATION', margin, 30);
  doc.setFontSize(12);
  doc.text('45 CFR § 164.526', margin, 40);

  // Dispute ID and Date
  doc.setFont('helvetica', 'normal');
  doc.text(`Dispute ID: ${disputeData.disputeId}`, margin, 55);
  doc.text(`Date: ${new Date().toLocaleDateString()}`, margin, 65);

  // Provider Information
  doc.setFont('helvetica', 'bold');
  doc.text('TO:', margin, 85);
  doc.setFont('helvetica', 'normal');
  doc.text(disputeData.providerName, margin, 95);
  if (disputeData.providerFax) {
    doc.text(`FAX: ${disputeData.providerFax}`, margin, 105);
  }

  // Patient Information
  doc.setFont('helvetica', 'bold');
  doc.text('PATIENT INFORMATION:', margin, 125);
  doc.setFont('helvetica', 'normal');
  doc.text(`Name: ${disputeData.patientName}`, margin, 135);
  doc.text(`Date of Birth: ${disputeData.patientDOB}`, margin, 145);
  
  // Handle multi-line address
  const addressLines = doc.splitTextToSize(`Address: ${disputeData.patientAddress}`, maxWidth);
  doc.text(addressLines, margin, 155);
  
  let yPosition = 155 + (addressLines.length * 10) + 20;

  // Records to be amended
  doc.setFont('helvetica', 'bold');
  doc.text('RECORDS TO BE AMENDED:', margin, yPosition);
  yPosition += 15;
  
  doc.setFont('helvetica', 'normal');
  disputeData.flaggedItems.forEach((item, index) => {
    const recordText = `${index + 1}. ${item.type.toUpperCase()}: ${item.title}`;
    const recordLines = doc.splitTextToSize(recordText, maxWidth);
    doc.text(recordLines, margin, yPosition);
    yPosition += recordLines.length * 10;
    
    const descText = `   Description: ${item.description}`;
    const descLines = doc.splitTextToSize(descText, maxWidth);
    doc.text(descLines, margin, yPosition);
    yPosition += descLines.length * 10;
    
    doc.text(`   Date: ${new Date(item.date).toLocaleDateString()}`, margin, yPosition);
    yPosition += 10;
    doc.text(`   Provider: ${item.provider}`, margin, yPosition);
    yPosition += 10;
    
    if (item.reason) {
      const reasonText = `   Issue: ${item.reason}`;
      const reasonLines = doc.splitTextToSize(reasonText, maxWidth);
      doc.text(reasonLines, margin, yPosition);
      yPosition += reasonLines.length * 10;
    }
    yPosition += 10;
  });

  // Check if we need a new page
  if (yPosition > 250) {
    doc.addPage();
    yPosition = 30;
  }

  // Requested Changes
  doc.setFont('helvetica', 'bold');
  doc.text('REQUESTED CHANGES:', margin, yPosition);
  yPosition += 15;
  
  doc.setFont('helvetica', 'normal');
  const changeLines = doc.splitTextToSize(disputeData.requestedChange, maxWidth);
  doc.text(changeLines, margin, yPosition);
  yPosition += changeLines.length * 10 + 20;

  // Justification
  doc.setFont('helvetica', 'bold');
  doc.text('JUSTIFICATION:', margin, yPosition);
  yPosition += 15;
  
  doc.setFont('helvetica', 'normal');
  const justificationLines = doc.splitTextToSize(disputeData.justification, maxWidth);
  doc.text(justificationLines, margin, yPosition);
  yPosition += justificationLines.length * 10 + 20;

  // Check if we need a new page for footer content
  if (yPosition > 200) {
    doc.addPage();
    yPosition = 30;
  }

  // Legal basis and response requirements
  doc.setFont('helvetica', 'bold');
  doc.text('LEGAL BASIS:', margin, yPosition);
  yPosition += 10;
  doc.setFont('helvetica', 'normal');
  const legalText = 'This request is made pursuant to 45 CFR § 164.526, which grants individuals the right to request amendment of protected health information maintained in a designated record set when the individual believes the information is inaccurate or incomplete.';
  const legalLines = doc.splitTextToSize(legalText, maxWidth);
  doc.text(legalLines, margin, yPosition);
  yPosition += legalLines.length * 10 + 20;

  // QR Code
  doc.addImage(qrCodeDataUrl, 'PNG', pageWidth - 70, yPosition, 50, 50);
  doc.setFontSize(10);
  doc.text('Scan to track dispute', pageWidth - 70, yPosition + 60);

  // Signature
  doc.setFontSize(12);
  doc.text(`Respectfully,`, margin, yPosition + 40);
  doc.text(disputeData.patientName, margin, yPosition + 55);

  return doc.output('blob');
};
